// src/components/CMRLoadModal.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "../axiosConfig";
import "../styles/CMRLoadModal.css";
import { useTranslation } from "react-i18next";
import {
  Trash2,
  Camera,
  Zap,
  ShieldCheck,
  Image as ImageIcon,
  CloudUpload,
  Plus,
  Maximize2,
  X,
  Upload,
  HardDrive,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import truckModalHero from "../assets/truckformodal.png";

// ✅ absolutize backend/object-storage photo paths
const API_ORIGIN =
  (axios.defaults?.baseURL || "").replace(/\/api\/?$/, "") || window.location.origin;

const toAbsolutePhotoUrl = (p) => {
  if (!p || typeof p !== "string") return null;
  if (/^https?:\/\//i.test(p)) return p;

  const clean = p.replace(/\/uploads\/uploads\//g, "/uploads/");

  if (clean.startsWith("/uploads/")) return `${API_ORIGIN}${clean}`;
  if (clean.startsWith("uploads/")) return `${API_ORIGIN}/${clean}`;

  return `${API_ORIGIN}/uploads/${clean.replace(/^\/+/, "")}`;
};

const normalizeDocPhotos = (arr) => {
  const safeArr = Array.isArray(arr) ? arr : [];

  return safeArr
    .map((item) =>
      typeof item === "string"
        ? {
            key: item,
            url: toAbsolutePhotoUrl(item) || item,
            sizeBytes: 0,
          }
        : {
            key: item?.key || item?.url || "",
            url: toAbsolutePhotoUrl(item?.url || item?.key || "") || "",
            sizeBytes: Number(item?.sizeBytes) || 0,
          }
    )
    .filter((photo) => photo?.url);
};

const MAX_TOTAL_UPLOAD_MB = 10;
const MAX_TOTAL_UPLOAD_BYTES = MAX_TOTAL_UPLOAD_MB * 1024 * 1024;

const WEB_PHOTO_MAX_DIMENSION = 1600;
const WEB_PHOTO_JPEG_QUALITY = 0.72;

const getDirectPhotoApiOrigin = () => {
  const hostname = String(
    window.location.hostname || ""
  ).toLowerCase();

  if (
    hostname ===
    "app.loadevo.com"
  ) {
    return "https://api.loadevo.com";
  }

  if (
    hostname ===
    "test-app.loadevo.com"
  ) {
    return "https://test-api.loadevo.com";
  }

  const configuredBaseUrl =
    String(
      axios.defaults?.baseURL ||
      ""
    ).trim();

  if (
    /^https?:\/\//i.test(
      configuredBaseUrl
    )
  ) {
    return configuredBaseUrl.replace(
      /\/api\/?$/,
      ""
    );
  }

  return "";
};

const DIRECT_PHOTO_API_ORIGIN =
  getDirectPhotoApiOrigin();

const normalizeStoredAuthToken = (
  value
) => {
  if (
    !value
  ) {
    return "";
  }

  let rawValue =
    value;

  try {
    const parsed =
      JSON.parse(
        String(
          value
        )
      );

    if (
      parsed &&
      typeof parsed ===
        "object"
    ) {
      rawValue =
        parsed.token ||
        parsed.accessToken ||
        parsed.authToken ||
        parsed.jwt ||
        "";
    }
  } catch {}

  return String(
    rawValue ||
    ""
  )
    .trim()
    .replace(
      /^Bearer\s+/i,
      ""
    )
    .replace(
      /^"|"$/g,
      ""
    );
};

const getUploadAuthToken = () => {
  const candidates = [
    localStorage.getItem(
      "token"
    ),
    sessionStorage.getItem(
      "token"
    ),
    localStorage.getItem(
      "authToken"
    ),
    sessionStorage.getItem(
      "authToken"
    ),
    localStorage.getItem(
      "accessToken"
    ),
    sessionStorage.getItem(
      "accessToken"
    ),
    localStorage.getItem(
      "auth_user"
    ),
    sessionStorage.getItem(
      "auth_user"
    ),
  ];

  for (
    const candidate of
    candidates
  ) {
    const token =
      normalizeStoredAuthToken(
        candidate
      );

    if (
      token
    ) {
      return token;
    }
  }

  return "";
};

const compressPhotoForUpload = (
  file
) => {
  return new Promise(
    (
      resolve
    ) => {
      if (
        !file ||
        !String(
          file.type ||
          ""
        ).startsWith(
          "image/"
        )
      ) {
        resolve(
          file
        );
        return;
      }

      const objectUrl =
        URL.createObjectURL(
          file
        );

      const image =
        new Image();

      image.onload =
        () => {
          try {
            const originalWidth =
              Number(
                image.naturalWidth
              ) ||
              Number(
                image.width
              ) ||
              0;

            const originalHeight =
              Number(
                image.naturalHeight
              ) ||
              Number(
                image.height
              ) ||
              0;

            if (
              !originalWidth ||
              !originalHeight
            ) {
              URL.revokeObjectURL(
                objectUrl
              );

              resolve(
                file
              );

              return;
            }

            const longestSide =
              Math.max(
                originalWidth,
                originalHeight
              );

            const scale =
              longestSide >
              WEB_PHOTO_MAX_DIMENSION
                ? WEB_PHOTO_MAX_DIMENSION /
                  longestSide
                : 1;

            const targetWidth =
              Math.max(
                1,
                Math.round(
                  originalWidth *
                    scale
                )
              );

            const targetHeight =
              Math.max(
                1,
                Math.round(
                  originalHeight *
                    scale
                )
              );

            const canvas =
              document.createElement(
                "canvas"
              );

            canvas.width =
              targetWidth;

            canvas.height =
              targetHeight;

            const context =
              canvas.getContext(
                "2d"
              );

            if (
              !context
            ) {
              URL.revokeObjectURL(
                objectUrl
              );

              resolve(
                file
              );

              return;
            }

            context.fillStyle =
              "#ffffff";

            context.fillRect(
              0,
              0,
              targetWidth,
              targetHeight
            );

            context.drawImage(
              image,
              0,
              0,
              targetWidth,
              targetHeight
            );

            canvas.toBlob(
              (
                blob
              ) => {
                URL.revokeObjectURL(
                  objectUrl
                );

                if (
                  !blob
                ) {
                  resolve(
                    file
                  );

                  return;
                }

                const baseName =
                  String(
                    file.name ||
                    "photo"
                  ).replace(
                    /\.[^/.]+$/,
                    ""
                  );

                const compressedFile =
                  new File(
                    [
                      blob,
                    ],
                    `${baseName}.jpg`,
                    {
                      type:
                        "image/jpeg",

                      lastModified:
                        file.lastModified ||
                        Date.now(),
                    }
                  );

                if (
                  compressedFile.size >=
                  file.size
                ) {
                  resolve(
                    file
                  );

                  return;
                }

                resolve(
                  compressedFile
                );
              },
              "image/jpeg",
              WEB_PHOTO_JPEG_QUALITY
            );
          } catch (
            error
          ) {
            URL.revokeObjectURL(
              objectUrl
            );

            console.warn(
              "[CMRLoadModal] Browser compression failed. Using original file.",
              error
            );

            resolve(
              file
            );
          }
        };

      image.onerror =
        () => {
          URL.revokeObjectURL(
            objectUrl
          );

          resolve(
            file
          );
        };

      image.src =
        objectUrl;
    }
  );
};

const formatBytesMB = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const withCacheBust = (url) => {
  if (!url) return "";
  return url;
};

export default function CMRLoadModal({
  isOpen,
  onClose,
  pickupDeliveryId,
  title,
  readOnly = false,
  footerText = null,
}) {
  const { t } = useTranslation("cmr_load_modal");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [docPhotos, setDocPhotos] = useState([]);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  // ✅ Amplify / Lightbox viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSrc, setViewerSrc] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);

  const canRender = isOpen && pickupDeliveryId;

  const previewFiles = useMemo(() => {
    if (readOnly) return [];
    return (files || []).map((f, idx) => ({
      idx,
      name: f.name,
      url: URL.createObjectURL(f),
    }));
  }, [files, readOnly]);

  const fetchDocPhotos = async () => {
    if (!pickupDeliveryId) return;

    setLoading(true);
    try {
      const res = await axios.get(`/pickupdeliveries/${pickupDeliveryId}/docPhotos`);
      const arr = Array.isArray(res.data?.docPhotos) ? res.data.docPhotos : [];
      const normalized = normalizeDocPhotos(arr);

      setDocPhotos(normalized);
      setUploadedBytes(
        Number(res.data?.usedBytes) ||
          normalized.reduce((sum, photo) => sum + (Number(photo?.sizeBytes) || 0), 0)
      );
    } catch (err) {
      console.error("CMRLoadModal fetchDocPhotos failed:", err);
      setDocPhotos([]);
      setUploadedBytes(0);
    } finally {
      setLoading(false);
    }
  };

  const openViewer = (url, idx = 0) => {
    const normalized = toAbsolutePhotoUrl(url) || url;
    setViewerSrc(normalized);
    setViewerIndex(idx);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerSrc(null);
  };

  const stepViewer = (dir) => {
    if (!docPhotos?.length) return;
    const next = (viewerIndex + dir + docPhotos.length) % docPhotos.length;
    const normalized = toAbsolutePhotoUrl(docPhotos[next]?.url) || null;
    setViewerIndex(next);
    setViewerSrc(normalized);
  };

  const downloadViewer = async () => {
    try {
      if (!viewerSrc) return;

      const extFromUrl = (() => {
        try {
          const u = new URL(viewerSrc, window.location.href);
          const clean = u.pathname.split("?")[0];
          const last = clean.split(".").pop();
          if (last && last.length <= 5) return last.toLowerCase();
        } catch {}
        return "jpg";
      })();

      const res = await fetch(viewerSrc, { mode: "cors" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `cmr-${pickupDeliveryId}-${viewerIndex + 1}.${extFromUrl}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error("Download failed:", e);
      alert(t("alerts.downloadFailed", { defaultValue: "❌ Failed to download image." }));
    }
  };

  const selectedTotalBytes = useMemo(() => {
    return (files || []).reduce((sum, f) => sum + (Number(f?.size) || 0), 0);
  }, [files]);

  const totalUsedBytes = uploadedBytes + selectedTotalBytes;
  const remainingBytes = Math.max(0, MAX_TOTAL_UPLOAD_BYTES - uploadedBytes);
  const willExceedTotalLimit = totalUsedBytes > MAX_TOTAL_UPLOAD_BYTES;

  const removePreviewAt = (idxToRemove) => {
    setFiles((prev) => {
      const next = (prev || []).filter((_, i) => i !== idxToRemove);

      const totalBytes = next.reduce((sum, f) => sum + (Number(f?.size) || 0), 0);

      if (totalBytes <= MAX_TOTAL_UPLOAD_BYTES) {
        setFileError("");
      }

      return next;
    });
  };

  const handleFileChange = async (e) => {
    const selectedFiles =
      Array.from(
        e.target.files ||
        []
      );

    if (e.target && "value" in e.target) {
      e.target.value = "";
    }

    if (!selectedFiles.length) {
      return;
    }

    setCompressing(true);
    setFileError("");

    try {
      const compressedFiles =
        await Promise.all(
          selectedFiles.map(
            (
              file
            ) =>
              compressPhotoForUpload(
                file
              )
          )
        );

      const totalSelectedBytes =
        compressedFiles.reduce(
          (
            sum,
            file
          ) =>
            sum +
            (
              Number(
                file?.size
              ) ||
              0
            ),
          0
        );

    const nextTotalUsedBytes =
      uploadedBytes +
      selectedTotalBytes +
      totalSelectedBytes;

    if (
      nextTotalUsedBytes >
      MAX_TOTAL_UPLOAD_BYTES
    ) {
      setFileError(
          t(
            "alerts.totalSizeExceeded",
            {
              maxMB:
                MAX_TOTAL_UPLOAD_MB,

              selectedMB:
                formatBytesMB(
                  totalSelectedBytes
                ),

              usedMB:
                formatBytesMB(
                  uploadedBytes
                ),

              totalMB:
                formatBytesMB(
                  nextTotalUsedBytes
                ),

              defaultValue:
                `⚠️ Total limit exceeded. Already uploaded: ${formatBytesMB(uploadedBytes)}. ` +
                `Selected now: ${formatBytesMB(totalSelectedBytes)}. ` +
                `Total would be: ${formatBytesMB(nextTotalUsedBytes)} / ${MAX_TOTAL_UPLOAD_MB} MB.`,
            }
          )
        );

        if (
          fileInputRef.current
        ) {
          fileInputRef.current.value =
            "";
        }

        return;
      }

      console.log(
        "[CMRLoadModal] photo compression",
        selectedFiles.map(
          (
            original,
            index
          ) => ({
            name:
              original.name,

            originalBytes:
              original.size,

            compressedBytes:
              compressedFiles[
                index
              ]?.size ||
              0,

            reduction:
              original.size
                ? `${Math.round(
                    (
                      1 -
                      (
                        compressedFiles[
                          index
                        ]?.size ||
                        original.size
                      ) /
                        original.size
                    ) *
                      100
                  )}%`
                : "0%",
          })
        )
      );

      setFiles((prev) => [
        ...prev,
        ...compressedFiles
      ]);
    } catch (
      error
    ) {
      console.error(
        "[CMRLoadModal] compression failed:",
        error
      );

      const originalSelectedBytes = selectedFiles.reduce(
        (sum, file) => sum + (Number(file?.size) || 0),
        0
      );

      if (
        uploadedBytes +
        selectedTotalBytes +
        originalSelectedBytes >
        MAX_TOTAL_UPLOAD_BYTES
      ) {
        setFileError(
          `O limite total de ${MAX_TOTAL_UPLOAD_MB} MB foi excedido.`
        );
      } else {
        setFiles((prev) => [
          ...prev,
          ...selectedFiles
        ]);
      }
    } finally {
      setCompressing(
        false
      );
    }
  };

  useEffect(() => {
    return () => {
      previewFiles.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch {}
      });
    };
  }, [previewFiles]);

  useEffect(() => {
    if (!canRender) return;
    fetchDocPhotos();
  }, [canRender, pickupDeliveryId]);

  useEffect(() => {
    if (!viewerOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") stepViewer(-1);
      if (e.key === "ArrowRight") stepViewer(1);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [viewerOpen, viewerIndex, docPhotos]);

  const uploadNow = async () => {
    if (readOnly) return;
    if (!files?.length) return;

    const currentSelectedBytes = (files || []).reduce(
      (sum, f) => sum + (Number(f?.size) || 0),
      0
    );

    const nextTotalUsedBytes = uploadedBytes + currentSelectedBytes;

    if (nextTotalUsedBytes > MAX_TOTAL_UPLOAD_BYTES) {
      const msg = t("alerts.totalSizeExceeded", {
        maxMB: MAX_TOTAL_UPLOAD_MB,
        selectedMB: formatBytesMB(currentSelectedBytes),
        usedMB: formatBytesMB(uploadedBytes),
        totalMB: formatBytesMB(nextTotalUsedBytes),
        defaultValue:
          `⚠️ Total limit exceeded. Already uploaded: ${formatBytesMB(uploadedBytes)}. ` +
          `Selected now: ${formatBytesMB(currentSelectedBytes)}. ` +
          `Total would be: ${formatBytesMB(nextTotalUsedBytes)} / ${MAX_TOTAL_UPLOAD_MB} MB.`,
      });

      setFileError(msg);
      alert(msg);
      return;
    }

    const fd = new FormData();
    (files || []).forEach((f) => fd.append("docPhotos", f));

    setSaving(true);
    try {
      const uploadPath =
        `/pickupdeliveries/${pickupDeliveryId}/upload/docPhotos`;

      const uploadOrigin =
        DIRECT_PHOTO_API_ORIGIN ||
        API_ORIGIN;

      const uploadUrl =
        `${uploadOrigin}${uploadPath}`;

      const uploadToken =
        getUploadAuthToken();

      if (
        !uploadToken
      ) {
        throw new Error(
          "Authentication token not found."
        );
      }

      const formFiles =
        fd.getAll(
          "docPhotos"
        );

      console.log(
        "[CMRLoadModal] upload payload",
        {
          uploadUrl,

          fileCount:
            formFiles.length,

          files:
            formFiles.map(
              (
                file
              ) => ({
                name:
                  file?.name ||
                  null,

                type:
                  file?.type ||
                  null,

                size:
                  file?.size ||
                  0,

                isFile:
                  file instanceof
                  File,

                isBlob:
                  file instanceof
                  Blob,
              })
            ),
        }
      );

      if (
        !formFiles.length
      ) {
        throw new Error(
          "No files found in FormData before upload."
        );
      }

      const uploadResponse =
        await fetch(
          uploadUrl,
          {
            method:
              "POST",

            headers: {
              Authorization:
                `Bearer ${uploadToken}`,
            },

            body:
              fd,
          }
        );

      const uploadPayload =
        await uploadResponse
          .json()
          .catch(
            () =>
              null
          );

      if (
        !uploadResponse.ok
      ) {
        throw new Error(
          uploadPayload?.error ||
          uploadPayload?.message ||
          `Photo upload failed (${uploadResponse.status}).`
        );
      }

      setFiles([]);
      setFileError("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await fetchDocPhotos();
    } catch (e) {
      console.error(
        "CMRLoadModal upload failed:",
        e
      );

      const serverMsg =
        e?.response?.data?.error ||
        e?.message ||
        t(
          "alerts.uploadFailed",
          {
            defaultValue:
              "❌ Failed to upload photos.",
          }
        );

      setFileError(
        serverMsg
      );

      alert(
        serverMsg
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canRender) return null;

  const computedTitle = (title ?? "").trim() || t("title", { defaultValue: "Photos" });

  const deletePhoto = async (photoToDelete) => {
    if (readOnly) return;

    const prev = docPhotos;
    setDocPhotos((cur) => (cur || []).filter((x) => x.key !== photoToDelete.key));

    if (
      viewerOpen &&
      (viewerSrc?.includes(photoToDelete.url) || viewerSrc === photoToDelete.url)
    ) {
      closeViewer();
    }

    try {
      const { data } = await axios.delete(
        `/pickupdeliveries/${pickupDeliveryId}/docPhotos`,
        { data: { src: photoToDelete.key } }
      );

      const updated = Array.isArray(data?.docPhotos) ? data.docPhotos : null;

      if (updated) {
        setDocPhotos(normalizeDocPhotos(updated));
      } else {
        await fetchDocPhotos();
      }
    } catch (e) {
      console.error("CMRLoadModal delete photo failed:", e);
      setDocPhotos(prev);
      alert(t("alerts.deleteFailed", { defaultValue: "❌ Failed to delete photo." }));
    }
  };

  return (
    <div className="cmrload-backdrop" onMouseDown={onClose}>
      <div
        className="cmrload-modal cmr-photo-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <aside className="cmr-photo-sidebar">
          <div className="cmr-photo-brand">
            <div className="cmr-photo-brand-icon">
              <Camera size={33} strokeWidth={2.6} />
            </div>

            <div className="cmr-photo-brand-text">
              <h2>{computedTitle}</h2>

              <p>
                {t("subtitle", {
                  defaultValue: "Gerir fotos deste ponto de carga/descarga",
                })}
              </p>
            </div>
          </div>

          <div className="cmr-photo-art" aria-hidden="true">
            <img
              className="cmr-photo-art-image is-back-left"
              src={truckModalHero}
              alt=""
              draggable={false}
            />

            <img
              className="cmr-photo-art-image is-back-right"
              src={truckModalHero}
              alt=""
              draggable={false}
            />

            <img
              className="cmr-photo-art-image is-front"
              src={truckModalHero}
              alt=""
              draggable={false}
            />
          </div>

          <div className="cmr-photo-benefits">
            <div className="cmr-photo-benefit">
              <span className="cmr-photo-benefit-icon">
                <Zap size={20} fill="currentColor" />
              </span>

              <div>
                <strong>Rápido e simples</strong>
                <span>Arraste ou selecione as fotos</span>
              </div>
            </div>

            <div className="cmr-photo-benefit">
              <span className="cmr-photo-benefit-icon">
                <ShieldCheck size={21} />
              </span>

              <div>
                <strong>Seguro</strong>
                <span>As fotos ficam guardadas com este ponto</span>
              </div>
            </div>

            <div className="cmr-photo-benefit">
              <span className="cmr-photo-benefit-icon">
                <ImageIcon size={21} />
              </span>

              <div>
                <strong>Formatos suportados</strong>
                <span>JPG, PNG, HEIC, WEBP</span>
                <small>Máx. 10 MB no total</small>
              </div>
            </div>
          </div>
        </aside>

        <section className="cmr-photo-main">
          <header className="cmr-photo-header">
            <div>
              <div className="cmr-photo-header-title">
                <h2>Fotos deste ponto</h2>
                <span>CMR</span>
              </div>

              <p>
                Adicione e gira as fotos da prova de carga/descarga
              </p>
            </div>

            <button
              type="button"
              className="cmr-photo-close"
              onClick={onClose}
              disabled={saving || compressing}
              aria-label="Fechar modal"
              title="Fechar"
            >
              <X size={21} />
            </button>
          </header>

          <div className="cmrload-body cmr-photo-body">
          <div className="cmrload-section">
            <div
              className={`cmrload-card ${
                !loading && !docPhotos.length ? "cmrload-card-compact" : ""
              }`}
            >
              <div className="cmr-photo-gallery-header">
                <div className="cmr-photo-gallery-heading">
                  <h3>
                    {t("sections.existingTitle", {
                      defaultValue: "Fotos existentes",
                    })}
                  </h3>

                  <span className="cmr-photo-count">
                    {docPhotos.length}
                  </span>
                </div>

                <button
                  type="button"
                  className="cmr-photo-gallery-button"
                  onClick={() => {
                    if (docPhotos.length > 0) {
                      openViewer(docPhotos[0].url, 0);
                    }
                  }}
                  disabled={loading || !docPhotos.length}
                >
                  <Maximize2 size={16} />
                  Ver em galeria
                </button>
              </div>

              <div className="cmrload-card-inner">
                {loading ? (
                  <div className="cmrload-muted">
                    {t("states.loading", { defaultValue: "Loading…" })}
                  </div>
                ) : docPhotos.length || previewFiles.length ? (
                  <div className="cmrload-grid">
                    {previewFiles.map((p) => (
                      <div
                        className="cmrload-thumb-wrap is-pending-photo"
                        key={`${p.name}-${p.idx}`}
                      >
                        <div className="cmrload-thumb cmrload-thumb-static">
                          <img
                            src={p.url}
                            alt={t("aria.previewAlt", {
                              name: p.name,
                              defaultValue: `Preview ${p.name}`,
                            })}
                          />
                        </div>

                        <button
                          type="button"
                          className="cmrload-thumb-delete"
                          onClick={() => removePreviewAt(p.idx)}
                          aria-label={t("aria.removePreview", {
                            defaultValue: "Remove photo",
                          })}
                          title={t("aria.removePreview", {
                            defaultValue: "Remove",
                          })}
                          disabled={saving}
                        >
                          <Trash2 size={16} strokeWidth={2.1} />
                        </button>
                      </div>
                    ))}

                    {docPhotos.map((photo, idx) => (
                      <div
                        className="cmrload-thumb-wrap"
                        key={`${photo.key || photo.url}-${idx}`}
                      >
                        <button
                          type="button"
                          className="cmrload-thumb"
                          onClick={() => openViewer(photo.url, idx)}
                          title={t("tooltips.enlarge", {
                            defaultValue: "Click to enlarge",
                          })}
                        >
                          <img
                            src={photo.url}
                            alt={t("aria.thumbAlt", {
                              idx: idx + 1,
                              defaultValue: `Photo ${idx + 1}`,
                            })}
                          />
                        </button>

                        {!readOnly && (
                          <button
                            type="button"
                            className="cmrload-thumb-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              deletePhoto(photo);
                            }}
                            aria-label={t("aria.delete", {
                              defaultValue: "Delete photo",
                            })}
                            title={t("aria.delete", { defaultValue: "Delete" })}
                            disabled={saving}
                          >
                            <Trash2 size={16} strokeWidth={2.1} />
                          </button>
                        )}
                      </div>
                    ))}

                    {!readOnly && (
                      <button
                        type="button"
                        className="cmr-photo-add-tile"
                        onClick={() =>
                          fileInputRef.current?.click()
                        }
                        disabled={saving || compressing}
                      >
                        <Plus size={36} strokeWidth={1.7} />
                        <span>
                          Adicionar
                          <br />
                          mais fotos
                        </span>
                      </button>
                    )}
                  </div>
                ) : (
                  readOnly ? (
                    <div className="cmrload-muted">
                      {t("states.empty", {
                        defaultValue: "Ainda não existem fotografias.",
                      })}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="cmr-photo-add-tile"
                      onClick={() =>
                        fileInputRef.current?.click()
                      }
                      disabled={saving || compressing}
                    >
                      <Plus size={36} strokeWidth={1.7} />
                      <span>Adicionar fotos</span>
                    </button>
                  )
                )}
              </div>
            </div>
          </div>

          {!readOnly ? (
            <div className="cmrload-section">
              <div className="cmrload-card">
                <div className="cmrload-section-title">
                  {t("sections.addTitle", { defaultValue: "Add new photos" })}
                </div>

                <div className="cmrload-card-inner">
                  <div
                    className="cmrload-upload-box cmr-photo-dropzone"
                    onClick={() => {
                      if (!saving && !compressing) {
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "copy";
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();

                      if (
                        saving ||
                        compressing ||
                        !event.dataTransfer.files.length
                      ) {
                        return;
                      }

                      void handleFileChange({
                        target: {
                          files: event.dataTransfer.files,
                        },
                      });
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      className="cmrload-file cmr-photo-file-input"
                      type="file"
                      accept="image/*"
                      multiple
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={handleFileChange}
                      disabled={saving || compressing}
                      aria-label={t("aria.fileInput", {
                        defaultValue: "Selecionar fotografias",
                      })}
                    />

                    <div className="cmr-photo-upload-symbol">
                      <CloudUpload size={34} strokeWidth={1.9} />
                    </div>

                    <strong className="cmr-photo-drop-title">
                      Arraste as fotos para aqui
                    </strong>

                    <span className="cmr-photo-drop-subtitle">
                      ou clique para selecionar
                    </span>

                    <button
                      type="button"
                      className="cmr-photo-choose-button"
                      disabled={saving || compressing}
                      onClick={(event) => {
                        event.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <ImageIcon size={19} />
                      Escolher Ficheiros
                    </button>

                    <small>
                      JPG, PNG, HEIC, WEBP
                      <span aria-hidden="true"> · </span>
                      Limite total de {MAX_TOTAL_UPLOAD_MB} MB
                    </small>
                  </div>

                  {!!fileError && (
                    <div className="cmrload-error-banner">{fileError}</div>
                  )}

                  <div className="cmrload-actions">
                    <button
                      className="cmrload-btn secondary"
                      onClick={onClose}
                      disabled={saving}
                    >
                      {t("buttons.close", { defaultValue: "Close" })}
                    </button>

                    <button
                      type="button"
                      className="cmrload-btn"
                      onClick={uploadNow}
                      disabled={
                        saving ||
                        compressing ||
                        !files?.length ||
                        willExceedTotalLimit
                      }
                    >
                      <Upload size={18} aria-hidden="true" />

                      <span>
                        {compressing
                          ? t("buttons.compressing", {
                              defaultValue: "A comprimir…",
                            })
                          : saving
                            ? t("buttons.uploading", {
                                defaultValue: "A carregar…",
                              })
                          : "Guardar"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="cmrload-section">
              <div className="cmrload-card">
                <div className="cmrload-actions cmrload-actions-single">
                  <button
                    className="cmrload-btn secondary"
                    onClick={onClose}
                    disabled={saving}
                  >
                    {footerText || t("buttons.close", { defaultValue: "Close" })}
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>

          <footer className="cmr-photo-footer">
            <div className="cmr-photo-storage">
              <span className="cmr-photo-storage-icon">
                <HardDrive size={20} />
              </span>

              <span className="cmr-photo-storage-label">
                <strong>{formatBytesMB(uploadedBytes)}</strong>
                {" "}utilizados de {MAX_TOTAL_UPLOAD_MB} MB
                {selectedTotalBytes > 0 && !readOnly && (
                  <small>
                    + {formatBytesMB(selectedTotalBytes)} selecionados
                  </small>
                )}
              </span>

              <div
                className="cmr-photo-storage-track"
                role="progressbar"
                aria-label="Armazenamento utilizado"
                aria-valuemin={0}
                aria-valuemax={MAX_TOTAL_UPLOAD_MB}
                aria-valuenow={
                  Math.min(
                    MAX_TOTAL_UPLOAD_MB,
                    uploadedBytes / (1024 * 1024)
                  )
                }
              >
                <span
                  style={{
                    width: `${Math.min(
                      100,
                      (uploadedBytes / MAX_TOTAL_UPLOAD_BYTES) * 100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </footer>
        </section>

        {viewerOpen && (
          <div className="cmrload-viewer-backdrop" onMouseDown={closeViewer}>
            <div className="cmrload-viewer" onMouseDown={(e) => e.stopPropagation()}>
              <div className="cmrload-viewer-tools">
                <button
                  type="button"
                  className="cmrload-viewer-download"
                  onClick={downloadViewer}
                  aria-label={t("aria.download", { defaultValue: "Download" })}
                  title={t("aria.download", { defaultValue: "Download" })}
                >
                  <Download size={19} strokeWidth={2} />
                </button>

                <button
                  type="button"
                  className="cmrload-viewer-close"
                  onClick={closeViewer}
                  aria-label={t("aria.close", { defaultValue: "Close" })}
                  title={t("aria.close", { defaultValue: "Close" })}
                >
                  <X size={20} strokeWidth={2} />
                </button>
              </div>

              {docPhotos.length > 1 && (
                <>
                  <button
                    type="button"
                    className="cmrload-viewer-nav left"
                    onClick={() => stepViewer(-1)}
                    aria-label={t("aria.prev", { defaultValue: "Previous" })}
                    title={t("aria.prev", { defaultValue: "Previous" })}
                  >
                    <ChevronLeft size={23} strokeWidth={2.2} />
                  </button>

                  <button
                    type="button"
                    className="cmrload-viewer-nav right"
                    onClick={() => stepViewer(1)}
                    aria-label={t("aria.next", { defaultValue: "Next" })}
                    title={t("aria.next", { defaultValue: "Next" })}
                  >
                    <ChevronRight size={23} strokeWidth={2.2} />
                  </button>
                </>
              )}

              <img
                className="cmrload-viewer-img"
                src={viewerSrc}
                alt={t("aria.viewerAlt", { defaultValue: "Preview" })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}